"use client"

import { useState } from "react"
import Image from "next/image"
import { Mic, PhoneOff, Sparkles } from "lucide-react"
import { CallTimer } from "@/components/call-timer"
import { useTranslations } from "next-intl"
import { getRatonPerezImageSources } from "@/lib/raton-perez-images"
import { CHARACTER_THEMES } from "@/lib/character-themes"
import { useDeferredHeroMedia } from "@/hooks/use-deferred-hero-media"

/** Hero preview mirrors the real Runway video call (character video + child self-view PiP). */
const RATON_VIDEO_SRC = "/videos/raton.mp4"

interface RatonPerezHeroVideoMockProps {
  videoSrc?: string
}

export default function RatonPerezHeroVideoMock({
  videoSrc = RATON_VIDEO_SRC,
}: RatonPerezHeroVideoMockProps) {
  const t = useTranslations("ratonPerezLanding")
  const landingT = useTranslations("landing")
  const { containerRef, isActive } = useDeferredHeroMedia()
  const [mainVideoLoaded, setMainVideoLoaded] = useState(false)
  const [pipVideoLoaded, setPipVideoLoaded] = useState(false)
  const imageSources = getRatonPerezImageSources()

  return (
    <div className="relative" ref={containerRef} style={{ contain: "layout style paint" }}>
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-400/20 to-amber-600/20 blur-2xl" />
      <div className={`relative overflow-hidden rounded-3xl border-2 border-white/15 ${CHARACTER_THEMES["raton-perez"].heroBg} shadow-2xl`}>
        <div className={`relative aspect-square w-full ${CHARACTER_THEMES["raton-perez"].heroBg}`} style={{ minHeight: "1px" }}>
          <picture className="absolute inset-0">
            <source srcSet={imageSources.webp} type="image/webp" />
            <img
              src={imageSources.png}
              alt={t("hero.characterName")}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className={`h-full w-full object-cover transition-opacity duration-300 ${
                isActive && mainVideoLoaded ? "opacity-0" : "opacity-100"
              }`}
              style={{ willChange: "opacity" }}
            />
          </picture>

          {isActive && (
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={imageSources.webp}
              preload="none"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                mainVideoLoaded ? "opacity-100" : "opacity-0"
              }`}
              style={{ willChange: "opacity" }}
              onLoadedData={() => {
                setMainVideoLoaded(true)
              }}
              onCanPlay={() => {
                setMainVideoLoaded(true)
              }}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}

          <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 backdrop-blur-sm">
            <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-amber-400" />
            <span className="text-sm font-medium text-white">{t("hero.callStatus")}</span>
            <CallTimer initialSeconds={98} className="text-sm text-white/80" />
          </div>

          <div className="absolute bottom-4 left-3 rounded-lg bg-black/60 px-3 py-2 backdrop-blur-sm">
            <p className="text-sm font-bold text-white">{t("hero.characterName")}</p>
            <p className="text-xs text-white/70">{t("hero.characterLocation")}</p>
            <p className="mt-1 text-xs font-medium text-amber-200">{t("hero.callBadge")}</p>
          </div>

          <div className="absolute right-3 top-3 h-24 w-[72px] overflow-hidden rounded-2xl border-2 border-amber-400/80 bg-black shadow-xl md:h-32 md:w-24">
            <Image
              src="/videos/child-pip-poster.webp"
              alt={landingT("hero.youLabel")}
              fill
              sizes="96px"
              className={`object-cover scale-x-[-1] transition-opacity duration-300 ${
                isActive && pipVideoLoaded ? "opacity-0" : "opacity-100"
              }`}
              loading="lazy"
            />
            {isActive && (
              <video
                autoPlay
                loop
                muted
                playsInline
                poster="/videos/child-pip-poster.webp"
                preload="none"
                className={`absolute inset-0 h-full w-full scale-x-[-1] object-cover transition-opacity duration-300 ${
                  pipVideoLoaded ? "opacity-100" : "opacity-0"
                }`}
                onLoadedData={() => {
                  setPipVideoLoaded(true)
                }}
                onCanPlay={() => {
                  setPipVideoLoaded(true)
                }}
              >
                <source src="/videos/child-pip.mp4" type="video/mp4" />
              </video>
            )}
            <div className="absolute bottom-1 left-1/2 z-10 -translate-x-1/2 rounded bg-black/70 px-2 py-0.5 text-[10px] text-white backdrop-blur-sm">
              {landingT("hero.youLabel")}
            </div>
          </div>

          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-sm shadow-lg md:h-12 md:w-12">
              <Mic className="h-5 w-5 text-white" />
            </div>

            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 shadow-lg md:h-14 md:w-14">
              <PhoneOff className="h-5 w-5 text-white md:h-6 md:w-6" />
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-sm shadow-lg md:h-12 md:w-12">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
